import React, { useState } from 'react';
import {
    Dialog, DialogContent, DialogActions, Box, Typography, TextField,
    Button, IconButton, InputAdornment, Alert, LinearProgress, CircularProgress,
} from '@mui/material';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK } from '../theme';
import http, { apiErrorMessage } from '../Api/http';
import { PostChangePassword } from '../Api/Api';

const MIN_LENGTH = 8;

const strengthOf = (pw) => {
    if (!pw) return { score: 0, label: '', color: '#E5E7EB' };
    let score = 0;
    if (pw.length >= MIN_LENGTH) score += 1;
    if (pw.length >= 12) score += 1;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
    if (/\d/.test(pw)) score += 1;
    if (/[^A-Za-z0-9]/.test(pw)) score += 1;
    if (score <= 2) return { score, label: 'Weak', color: '#DC2626' };
    if (score === 3) return { score, label: 'Fair', color: '#F59E0B' };
    if (score === 4) return { score, label: 'Good', color: '#0EA5E9' };
    return { score, label: 'Strong', color: '#16A34A' };
};

const field = {
    '& .MuiOutlinedInput-root': { borderRadius: '9px', fontSize: 13.5, bgcolor: '#fff' },
    '& .MuiInputLabel-root': { fontSize: 13.5 },
};

export default function ChangePasswordDialog({ open, onClose, onDone }) {
    const [form, setForm] = useState({ current: '', next: '', confirm: '' });
    const [show, setShow] = useState({ current: false, next: false, confirm: false });
    const [tried, setTried] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(''); };
    const toggle = (k) => () => setShow((s) => ({ ...s, [k]: !s[k] }));

    const strength = strengthOf(form.next);
    const tooShort = Boolean(form.next) && form.next.length < MIN_LENGTH;
    const sameAsCurrent = Boolean(form.next) && form.next === form.current;
    const mismatch = Boolean(form.confirm) && form.confirm !== form.next;
    const valid = form.current && form.next && form.confirm
        && !tooShort && !sameAsCurrent && !mismatch;

    const reset = () => {
        setForm({ current: '', next: '', confirm: '' });
        setShow({ current: false, next: false, confirm: false });
        setTried(false); setError(''); setDone(false); setSaving(false);
    };

    const close = () => { if (!saving) { reset(); onClose(); } };

    const submit = async () => {
        setTried(true);
        if (!valid) return;
        setSaving(true);
        setError('');
        try {
            const { data: body } = await http.post(PostChangePassword, {
                currentPassword: form.current,
                newPassword: form.next,
                confirmPassword: form.confirm,
            });
            if (body?.error) throw new Error(body.message || 'Could not change the password.');
            setDone(true);
        } catch (err) {
            setError(apiErrorMessage(err, 'Could not change the password.'));
        } finally {
            setSaving(false);
        }
    };

    if (done) {
        return (
            <Dialog open={open} onClose={() => {}} maxWidth="xs" fullWidth
                slotProps={{ paper: { sx: { borderRadius: '16px', p: 0.5 } } }}>
                <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
                    <Box sx={{
                        width: 62, height: 62, borderRadius: '50%', mx: 'auto', mb: 2,
                        bgcolor: '#DCFCE7', border: '1px solid #BBF7D0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <CheckCircleRoundedIcon sx={{ fontSize: 32, color: '#16A34A' }} />
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Password changed</Typography>
                    <Typography sx={{ fontSize: 13.5, color: '#6B7280', mt: 0.8, px: 1 }}>
                        For your security every signed-in device has been logged out.
                        Please sign in again with your new password.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
                    <Button
                        onClick={() => { reset(); onDone(); }}
                        fullWidth
                        sx={{
                            textTransform: 'none', fontWeight: 700, fontSize: 14, height: 44,
                            borderRadius: '10px', color: '#fff', bgcolor: PRIMARY, boxShadow: 'none',
                            '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
                        }}
                    >
                        Go to sign in
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={close} maxWidth="xs" fullWidth
            slotProps={{ paper: { sx: { borderRadius: '16px', p: 0.5 } } }}>
            <DialogContent sx={{ pt: 3, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 2.2 }}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '11px',
                        bgcolor: PRIMARY_LIGHT, color: PRIMARY,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <LockResetRoundedIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: '#0F172A' }}>Change password</Typography>
                        <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                            You&apos;ll be signed out on every device afterwards.
                        </Typography>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 1.8, borderRadius: '9px', fontSize: 12.5, py: 0.4 }}>
                        {error}
                    </Alert>
                )}

                <TextField
                    label="Current password"
                    type={show.current ? 'text' : 'password'}
                    value={form.current}
                    onChange={set('current')}
                    size="small" fullWidth autoComplete="current-password"
                    error={tried && !form.current}
                    helperText={tried && !form.current ? 'Enter your current password' : ' '}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={toggle('current')} edge="end" size="small" tabIndex={-1}>
                                        {show.current ? <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} /> : <VisibilityRoundedIcon sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={field}
                />

                <TextField
                    label="New password"
                    type={show.next ? 'text' : 'password'}
                    value={form.next}
                    onChange={set('next')}
                    size="small" fullWidth autoComplete="new-password"
                    error={(tried && !form.next) || tooShort || sameAsCurrent}
                    helperText={
                        tooShort ? `At least ${MIN_LENGTH} characters`
                            : sameAsCurrent ? 'Must be different from your current password'
                                : (tried && !form.next) ? 'Enter a new password' : ' '
                    }
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={toggle('next')} edge="end" size="small" tabIndex={-1}>
                                        {show.next ? <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} /> : <VisibilityRoundedIcon sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={field}
                />

                {form.next && (
                    <Box sx={{ mt: -1.4, mb: 1.4, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(100, (strength.score / 5) * 100)}
                            sx={{
                                flex: 1, height: 5, borderRadius: 4, bgcolor: '#EEF0F6',
                                '& .MuiLinearProgress-bar': { bgcolor: strength.color, borderRadius: 4 },
                            }}
                        />
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: strength.color, minWidth: 42, textAlign: 'right' }}>
                            {strength.label}
                        </Typography>
                    </Box>
                )}

                <TextField
                    label="Confirm new password"
                    type={show.confirm ? 'text' : 'password'}
                    value={form.confirm}
                    onChange={set('confirm')}
                    onKeyDown={(e) => { if (e.key === 'Enter' && valid && !saving) submit(); }}
                    size="small" fullWidth autoComplete="new-password"
                    error={(tried && !form.confirm) || mismatch}
                    helperText={
                        mismatch ? 'The two passwords don’t match'
                            : (tried && !form.confirm) ? 'Re-enter the new password' : ' '
                    }
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={toggle('confirm')} edge="end" size="small" tabIndex={-1}>
                                        {show.confirm ? <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} /> : <VisibilityRoundedIcon sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={field}
                />
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5, gap: 1 }}>
                <Button
                    onClick={close}
                    disabled={saving}
                    fullWidth
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: 14, height: 44, borderRadius: '10px', color: '#334155', bgcolor: '#F1F5F9', boxShadow: 'none', '&:hover': { bgcolor: '#E2E8F0', boxShadow: 'none' } }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={submit}
                    disabled={saving}
                    fullWidth
                    startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <LockResetRoundedIcon sx={{ fontSize: 18 }} />}
                    sx={{
                        textTransform: 'none', fontWeight: 700, fontSize: 14, height: 44,
                        borderRadius: '10px', color: '#fff', bgcolor: PRIMARY, boxShadow: 'none',
                        '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
                        '&.Mui-disabled': { bgcolor: '#CFE0EC', color: '#fff' },
                    }}
                >
                    {saving ? 'Changing…' : 'Change password'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
